import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { resolveDefaultLanguage } from "@fiket/booth-schema";
import { useRouter, type Route } from "./store/router";
import { useBoothConfig, extractBoothSlug } from "./store/booth";
import { AppShell } from "./components/shell/AppShell";
import { AttractView } from "./features/Attract/AttractView";
import { ThemeSelectView } from "./features/ThemeSelect/ThemeSelectView";
import { StyleSelectView } from "./features/StyleSelect/StyleSelectView";
import { PreviewView } from "./features/Preview/PreviewView";
import { GenerateView } from "./features/Generate/GenerateView";
import { ReviewView } from "./features/Review/ReviewView";
import { VideoIntroView } from "./features/Video/VideoIntroView";
import { VideoMotionsView } from "./features/Video/VideoMotionsView";
import { VideoCaptionView } from "./features/Video/VideoCaptionView";
import { VideoGenerateView } from "./features/Video/VideoGenerateView";
import { PaymentView } from "./features/Payment/PaymentView";
import { PrintShareView } from "./features/PrintShare/PrintShareView";
import { PrintView } from "./features/Print/PrintView";
import { PrintSheet } from "./features/Print/PrintSheet";
import { ThanksView } from "./features/Thanks/ThanksView";
import { LockView } from "./features/Lock/LockView";
import { ConsentView } from "./features/Consent/ConsentView";
import { CameraProvider, useCameraContext } from "./camera/CameraProvider";
import { setActivityBoothSlug } from "./telemetry/activity";
import { installConnectivityListeners, startHeartbeat, trackBoot } from "./telemetry/presence";

const TRANSITION = { duration: 0.35, ease: [0.32, 0.72, 0, 1] as const };

export default function App() {
  return (
    <CameraProvider>
      <AppInner />
    </CameraProvider>
  );
}

function AppInner() {
  const route = useRouter((s) => s.route);
  const language = useRouter((s) => s.language);
  const camera = useCameraContext();

  // <html lang> 을 store 와 동기화 — globals.css 의 :root:lang(ja|zh) 서체 스왑·줄바꿈 규칙이 이 속성에 매달린다 (BOOTH-30).
  // 첫 페인트는 main.tsx 가 render 전에 맞춘다.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  const { loaded: boothLoaded, config, error: boothError, load, refresh } = useBoothConfig();

  // 부스 기본 언어 (BOOTH-116) — 어트랙트면 즉시, 손님 세션 중이면 resetSession 때 적용. 콘솔 변경은 어트랙트 복귀 refresh 로 다음 손님부터.
  const setBoothLanguage = useRouter((s) => s.setBoothLanguage);
  const boothDefaultLanguage = config ? resolveDefaultLanguage(config.booth) : null;
  useEffect(() => {
    if (boothDefaultLanguage) setBoothLanguage(boothDefaultLanguage);
  }, [boothDefaultLanguage, setBoothLanguage]);

  // URL에서 slug 추출 후 부스 설정 로드 (slug 없으면 "default"). 활동 이벤트 전송 엔드포인트도 같은 slug (BOOTH-87).
  // 부팅·하트비트·온오프라인 상태 신호도 같은 effect 에서 시작한다 (BOOTH-97) — 부팅은 페이지 로드당 1회(StrictMode 이중 effect 무시).
  useEffect(() => {
    const slug = extractBoothSlug() ?? "default";
    setActivityBoothSlug(slug);
    trackBoot();
    const stopHeartbeat = startHeartbeat();
    const stopConnectivity = installConnectivityListeners();
    void load(slug);
    return () => {
      stopHeartbeat();
      stopConnectivity();
    };
  }, [load]);

  // 어트랙트로 돌아올 때마다 조용히 재조회 — 콘솔에서 바꾼 인쇄 프리셋·스타일을 다음 손님부터 반영 (BOOTH-35)
  useEffect(() => {
    if (route === "attract") void refresh();
  }, [route, refresh]);

  const enabled = config?.booth.enabled ?? true;

  // 카메라는 촬영 스테이지에 "진입할 때" 한 번 켠다.
  // camera 객체 전체를 deps 에 넣으면 status 변화(error 등)마다 재실행되어
  // 에러 → start → requesting → 에러 … 무한 재시도 루프가 생긴다. start/stop 은 안정 참조.
  const { start: startCamera, stop: stopCamera } = camera;
  useEffect(() => {
    if (enabled && route === "preview") {
      void startCamera();
    } else {
      stopCamera();
    }
  }, [route, enabled, startCamera, stopCamera]);

  const stageKey = !boothLoaded ? "__loading" : boothError ? "__error" : !enabled ? "__locked" : route;
  const shellRoute: Route | null = boothLoaded && !boothError && enabled ? route : null;

  return (
    <>
    <AppShell route={shellRoute}>
      <AnimatePresence mode="wait">
        <motion.div
          key={stageKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={TRANSITION}
          className="absolute inset-0"
        >
          {!boothLoaded ? null : boothError ? (
            <div className="view center"><p>{boothError}</p></div>
          ) : !enabled ? <LockView /> : <StageView route={route} />}
        </motion.div>
      </AnimatePresence>
    </AppShell>
    {/* 인쇄 시트는 .app 의 형제 — @media print 에서 .app 을 숨기고 사진만 인쇄한다. agent 모드는 window.print 를 안 쓰므로 생략 */}
    {route === "print" && config?.booth.printMode !== "agent" ? <PrintSheet /> : null}
    </>
  );
}

function StageView({ route }: { route: Route }) {
  switch (route) {
    case "attract":       return <AttractView />;
    case "themeSelect":   return <ThemeSelectView />;
    case "styleSelect":   return <StyleSelectView />;
    case "consent":       return <ConsentView />;
    case "preview":       return <PreviewView />;
    case "generate":      return <GenerateView />;
    case "review":        return <ReviewView />;
    case "videoIntro":    return <VideoIntroView />;
    case "videoMotions":  return <VideoMotionsView />;
    case "videoCaption":  return <VideoCaptionView />;
    case "videoGenerate": return <VideoGenerateView />;
    case "payment":       return <PaymentView />;
    case "printShare":    return <PrintShareView />;
    case "print":         return <PrintView />;
    case "thanks":        return <ThanksView />;
  }
}
